import { isEmailLoggedOut, type Email } from "@/pages/Private/pages/Account/schema/email";

import type { User } from "@/pages/Public/pages/Login/schema/login";

import type { SenderRecipientOption } from "../components/SenderRecipientDropdown";

/** The columns of a mailbox the default reads. */
export type DefaultMailboxCandidate = Pick<Email, "id" | "email" | "type" | "status" | "active">;

/**
 * Whether a mailbox can be offered as a sender: not logged out (`isEmailLoggedOut`: CREDENTIALS or
 * DELETED, the app's one definition) and switched on. Unipile's in-between statuses on a working
 * mailbox (CREATION_SUCCESS, SYNC_SUCCESS, CONNECTING) are healthy: every new mailbox reports them
 * for hours before OK. A logged-out mailbox is never a default and is shown disabled in the
 * composer's picker (PREC-4312, E3).
 */
export const isMailboxHealthy = (mailbox: DefaultMailboxCandidate): boolean =>
	!isEmailLoggedOut(mailbox.status) && mailbox.active !== false;

/**
 * The one email rule (PREC-4312, E8): the healthy mailbox tagged primary, else the first healthy
 * one. The composer's default, the GenerateBar and the feed's direct generation all read it.
 */
export const pickDefaultMailbox = <M extends DefaultMailboxCandidate>(
	emails: M[] | null | undefined
): M | undefined => {
	const healthy = (emails ?? []).filter(isMailboxHealthy);

	return healthy.find((mailbox) => mailbox.type === "primary") ?? healthy[0];
};

/** The current user's entry among the sending users; undefined for a user with no sender. */
export const findMeAmongSenders = (
	sendingUsers: User[] | null | undefined,
	currentUserId: number | null | undefined
): User | undefined =>
	currentUserId == null ? undefined : sendingUsers?.find((u) => u.id === currentUserId);

export type MyDefaultSender =
	/** The LinkedIn connection check has not answered, and its answer decides. */
	| { kind: "pending" }
	/** I have no channel that reaches this lead: the composer stays on "Select sender". */
	| { kind: "none" }
	| { kind: "linkedin"; userId: number; invite: boolean }
	| { kind: "email"; userId: number; emailId: number; emailAddress: string };

export interface ResolveMyDefaultSenderParams {
	/** The current user's sending-user entry (see {@link findMeAmongSenders}). */
	me: User | undefined;
	prospectHasLinkedinUrl: boolean;
	prospectHasEmail: boolean;
	connectionCheckDone: boolean;
	connectedUserIds: Set<number>;
}

/**
 * Who sends a new message to a lead when nothing has been said to it yet (PREC-4312). The default
 * is always me or nothing — never a colleague — in this order (E1):
 *
 *   1. my LinkedIn, if I am connected to the lead;
 *   2. otherwise my primary email (see {@link pickDefaultMailbox}), if the lead has an address;
 *   3. otherwise my LinkedIn as a connection invite.
 *
 * Because step 1 depends on the connection check, nothing is decided while it is in flight for a
 * lead I could reach on LinkedIn: deciding early used to turn the first draft into an invite
 * although I could have emailed.
 */
export const resolveMyDefaultSender = ({
	me,
	prospectHasLinkedinUrl,
	prospectHasEmail,
	connectionCheckDone,
	connectedUserIds,
}: ResolveMyDefaultSenderParams): MyDefaultSender => {
	if (!me) {
		return { kind: "none" };
	}

	const canLinkedin = prospectHasLinkedinUrl && !!me.unipile?.isLoggedIn;

	if (canLinkedin && !connectionCheckDone) {
		return { kind: "pending" };
	}

	if (canLinkedin && connectedUserIds.has(me.id)) {
		return { kind: "linkedin", userId: me.id, invite: false };
	}

	const mailbox = prospectHasEmail ? pickDefaultMailbox(me.emails) : undefined;

	if (mailbox) {
		return { kind: "email", userId: me.id, emailId: mailbox.id, emailAddress: mailbox.email };
	}

	if (canLinkedin) {
		return { kind: "linkedin", userId: me.id, invite: true };
	}

	return { kind: "none" };
};

/**
 * My own composer option on a channel, by the same rule: my LinkedIn, or my default mailbox
 * ({@link pickDefaultMailbox}); never a colleague's, never a disabled option. Used wherever a
 * draft has to move to a channel the user did not pick a sender for (a recipient switch, an AI
 * draft without a generation sender).
 */
export const pickMyOption = (
	options: SenderRecipientOption[],
	channel: "email" | "linkedin",
	me: User | undefined
): SenderRecipientOption | undefined => {
	if (!me) {
		return undefined;
	}

	const emailId = channel === "email" ? pickDefaultMailbox(me.emails)?.id : undefined;

	return options.find(
		(o) =>
			o.type === channel &&
			o.userId === me.id &&
			!o.disabled &&
			(channel === "linkedin" || (!!emailId && o.emailId === emailId))
	);
};
